import {
    type ApiEndpoint,
    type DownloadUrlResponse,
    isDownloadUrlResponse,
} from "@/shared/api-endpoint";
import type { MusicalKey } from "@/shared/musical-key.api";
import type { ParameterRange } from "@/shared/parameter-range";
import { kDiatonicModeLabels, kDiatonicModes } from "@/shared/scale-profile";
import { isObject } from "@/shared/utils/typeGuards";

export const kKeyChangerRoute = "/key-changer";

// Key Changer runs a markedly lower complexity default than the other v3 apps
// (which share `kComplexity30Range` at 0.644) — this mirrors `COMPLEXITY_DEFAULT`
// in `serving/apps/key_changer/app.py`. It is the top-k C_max threshold; as a
// float it is a cumulative-energy fraction.
export const kKeyChangerComplexityRange: ParameterRange = {
    min: 0,
    max: 1,
    default: 0.25804267078638077,
};

// Chroma permutation solvers, from `KeyChangerInput.constraint`:
// "permute" (Hungarian + 2-opt), "shift" (circular only, much cheaper), "sort", "argmax".
export const kKeyChangerConstraints = ["permute", "shift", "sort", "argmax"] as const;
export type KeyChangerConstraint = (typeof kKeyChangerConstraints)[number];
export const kKeyChangerConstraintDefault: KeyChangerConstraint = "permute";

const isKeyChangerConstraint = (value: unknown): value is KeyChangerConstraint =>
    typeof value === "string" && (kKeyChangerConstraints as readonly string[]).includes(value);

// `KeyChangerInput.quality`, which trades quality for latency (`CFG.quality_config`
// in audio-science-genai's key_changer). "high" is the original setting and the
// default; "low" and "medium" take 8 steps without CFG, and "medium" adds a
// latent post-filter.
export const kKeyChangerQualities = ["low", "medium", "high"] as const;
export type KeyChangerQuality = (typeof kKeyChangerQualities)[number];
export const kKeyChangerQualityDefault: KeyChangerQuality = "high";

export const isKeyChangerQuality = (value: unknown): value is KeyChangerQuality =>
    typeof value === "string" && (kKeyChangerQualities as readonly string[]).includes(value);

/**
 * Harmony roots. Upstream these are the `audio_key` strings; `ChromaScaleBuilder`
 * accepts sharps and flats for the same pitch class, so the sharp spelling from
 * `kMusicalKeys` passes straight through. The empty member of `MusicalKey` is
 * excluded — a lane entry always names a root.
 */
export type KeyChangerRoot = Exclude<MusicalKey, "">;
export const kKeyChangerRoots: readonly KeyChangerRoot[] = [
    "c",
    "c#",
    "d",
    "d#",
    "e",
    "f",
    "f#",
    "g",
    "g#",
    "a",
    "a#",
    "b",
];

/**
 * Scale profiles. Upstream calls this `chord_type`, but it indexes
 * `ChromaScaleBuilder.profiles` — the seven diatonic modes from
 * `generate_standard_modes()` (with `major`/`minor` as aliases for
 * ionian/aeolian), plus two harmonic-minor rotations from
 * `generate_harmonic_minor_modes()`. The mode name is the wire value, so no
 * conversion is needed.
 *
 * The harmonic-minor pair earns its place by unlocking dominant and diminished
 * colour the diatonic set cannot express — in an A minor context, Phrygian
 * Dominant on E gives E7♭9 and Ultralocrian on G♯ gives G♯dim.
 *
 * The builder knows a wider vocabulary still (pentatonic, blues, hexatonic,
 * melodic minor, sixdim), which this endpoint accepts without an API change.
 */
export const kKeyChangerModes = [
    ...kDiatonicModes,
    "harmonic_minor_phrygian_dominant",
    "harmonic_minor_ultralocrian",
] as const;

export type KeyChangerMode = (typeof kKeyChangerModes)[number];

/** Dropdown and lane-block labels for every mode above. */
export const kKeyChangerModeLabels: Record<KeyChangerMode, string> = {
    ...kDiatonicModeLabels,
    harmonic_minor_phrygian_dominant: "Phrygian Dominant",
    harmonic_minor_ultralocrian: "Ultralocrian",
};

const isKeyChangerRoot = (value: unknown): value is KeyChangerRoot =>
    typeof value === "string" && (kKeyChangerRoots as readonly string[]).includes(value);

const isKeyChangerMode = (value: unknown): value is KeyChangerMode =>
    typeof value === "string" && (kKeyChangerModes as readonly string[]).includes(value);

// Permutation chunk length in bars at the request's BPM: 1.0 is one chord per
// bar, 0.5 a half note, and so on down to a sixteenth. The model also accepts
// 0 (one chunk per chroma frame), which this UI does not offer.
//
// This is the fallback for an omitted field, and it matches the model's own
// `chunk_bars` default so a direct API caller gets what the model documents.
export const kChunkBarsDefault = 1;

/**
 * What the pane opens on. Deliberately not the wire default of one bar, and not
 * the centre of the Variation slider's nine-point scale (1/16 … 16 bars): it
 * matches the chord lane's quarter-note commit grid, so out of the box every
 * commit lands exactly on a chunk boundary with nothing landing late or
 * rendering shadowed.
 */
export const kChunkBarsUiDefault = 0.25;

export interface KeyChangerRequestBody {
    // The track being retuned. It keeps its own material and rhythm — unlike
    // magic fit there is no inversion step, its chroma is permuted onto the target.
    timbreAudioKey: string;
    bpm: number;
    /**
     * Harmony source 1: another track, whose chroma drives the harmony chunk by
     * chunk. Output length still follows the timbre track; a shorter driver
     * holds its last harmony to the end, and its rests hold the harmony before.
     */
    drivingAudioKey?: string;
    /**
     * Harmony source 2: a root + scale-profile progression, one entry per chunk,
     * looped to fill the clip. The two lanes are independent — upstream loops
     * them with separate modulo — so they need not be the same length.
     */
    audioKeys?: KeyChangerRoot[];
    chordTypes?: KeyChangerMode[];
    constraint?: KeyChangerConstraint;
    quality?: KeyChangerQuality;
    chunkBars?: number;
    complexity?: number;
    seed?: number;
}

const isNonEmptyArrayOf = <T>(
    value: unknown,
    predicate: (item: unknown) => item is T,
): value is T[] => Array.isArray(value) && value.length > 0 && value.every(predicate);

/**
 * Exactly one harmony source. Upstream accepts both without complaint and then
 * silently ignores the driver (`retune` returns on the `audio_key` branch
 * first), so rejecting the ambiguous request here is the only way a caller
 * learns which algorithm it would have got.
 */
export const hasChordLane = (value: { audioKeys?: unknown; chordTypes?: unknown }): boolean =>
    isNonEmptyArrayOf(value.audioKeys, isKeyChangerRoot) &&
    isNonEmptyArrayOf(value.chordTypes, isKeyChangerMode);

export const isKeyChangerRequestBody = (value: unknown): value is KeyChangerRequestBody => {
    if (
        !isObject(value) ||
        typeof value.timbreAudioKey !== "string" ||
        typeof value.bpm !== "number" ||
        !(value.constraint === undefined || isKeyChangerConstraint(value.constraint)) ||
        !(value.quality === undefined || isKeyChangerQuality(value.quality)) ||
        !(value.chunkBars === undefined || typeof value.chunkBars === "number") ||
        !(value.complexity === undefined || typeof value.complexity === "number") ||
        !(value.seed === undefined || typeof value.seed === "number")
    ) {
        return false;
    }
    const hasDriver = typeof value.drivingAudioKey === "string";
    const hasLane = hasChordLane(value);
    // A partially filled lane (one list, or an unknown root/mode) fails here
    // rather than falling through to driver mode.
    const hasLaneInput = value.audioKeys !== undefined || value.chordTypes !== undefined;
    if (hasLaneInput && !hasLane) {
        return false;
    }
    return hasDriver !== hasLane;
};

export const kKeyChangerDescription =
    "Retunes a sample keeping its timbre, material, and rhythm intact.";

export const kKeyChangerEndpoint: ApiEndpoint<KeyChangerRequestBody, DownloadUrlResponse> = {
    route: kKeyChangerRoute,
    label: "Key Changer",
    source: "Modal",
    isEvaluationOnly: true,
    category: "SpliceGen v3.5 key",
    description: kKeyChangerDescription,
    isRequestBody: isKeyChangerRequestBody,
    isSuccessResponse: isDownloadUrlResponse,
};
